import { useEffect, useRef, useState } from 'react';
import { PageHeader } from '../../PageHeader';
import { CONNECTORS_PAGE, useMcpController, type AppsController } from '../../../../lib/use-connectors';
import { CONNECTORS_TABS, connectorsTargetElementId, type ConnectorsRoute, type ConnectorsTab } from '../../../../lib/connectors-route';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import { useTranslation } from '../../../../lib/i18n';
import { useWhatsappGroups } from '../../../settings/shared/settings-queries';
import { useAgentConnections } from '../../../../lib/agent-connections';
import { ConnectorsGenomeMobile } from './ConnectorsGenomeMobile';
import { ToolsMobile } from './ToolsMobile';
import { ChannelsMobile } from './ChannelsMobile';
import { Input, Tabs } from '../../ui';
import { ChannelsOutBody } from '../ChannelsOutBody';
import { Spinner } from '../../../Spinner';
import styles from './mobile.module.css';

/**
 * The mobile Connectors & MCP's screen (Figma 13131-78167 / 13619-121017).
 *
 * A separate presentation from the desktop page that consumes the SAME shared logic: the single
 * `AppsController` the page owns, the shared route, and the shared channel sub-components. Layout:
 * title + blurb, the hero, a Tools/Channels tab bar, a tools search (Tools only), then the section.
 *
 * The whole screen is held behind ONE loading state until every connectors API has data, so the hero
 * and both tabs reveal fully-formed instead of each section popping in one fetch at a time. The four
 * reads: `apps` (connectors — hero + Tools) and the MCP list feed Tools; WhatsApp groups + agent
 * connections feed Channels. The WhatsApp/connection reads are React-Query and dedupe with the same
 * reads inside the sections, so observing them here adds no request; the MCP controller is lifted here
 * (it is `useState`-based, so it must be owned once) and passed down to the MCP section.
 */
export function ConnectorsMobile({
  apps,
  route,
  initialTab,
  onSelectTab,
}: {
  apps: AppsController;
  route: ConnectorsRoute;
  initialTab: ConnectorsTab;
  onSelectTab: (tab: ConnectorsTab) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [tab, setTab] = useState<ConnectorsTab>(initialTab);
  const [query, setQuery] = useState('');
  // The channels query is its OWN state: switching tabs must not carry a tool name into the channels
  // list (or back). Mirrors iOS/Android, where the channels search is independent of the tools one.
  const [channelQuery, setChannelQuery] = useState('');
  const tabsRef = useRef<HTMLDivElement>(null);

  const mcp = useMcpController();
  const whatsapp = useWhatsappGroups();
  const connections = useAgentConnections();
  useLoadBi('mcp-list', { isLoading: mcp.state.kind === 'loading', isError: mcp.state.kind === 'error' });

  // An errored read falls through to the content (which shows its own inline error) — only a genuine
  // in-flight read holds the spinner.
  const loading =
    apps.state.kind === 'loading' || mcp.state.kind === 'loading' || whatsapp.loading || connections.loading;

  // A live server push / deep link that targets a specific channel or tool switches to that tab so
  // the target is on screen.
  useEffect(() => {
    if (route.target) setTab(route.tab);
  }, [route.target, route.tab]);

  useEffect(() => {
    if (loading || tab !== 'tools' || route.target !== 'shopify-direct') return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(connectorsTargetElementId('tools', route.target))
      ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  }, [loading, tab, route.target]);

  // The tab bar sits below the hero, so switching tabs — from the bar OR the hero's "+N" chips —
  // brings it into view; otherwise the change happens off-screen.
  const revealTabs = () => {
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    tabsRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  const selectTab = (next: ConnectorsTab) => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: `tab_${next}` });
    setTab(next);
    onSelectTab(next);
    revealTabs();
  };

  return (
    <>
      <PageHeader title={t('connectors.mcpTitle')} description={t('connectors.description.symphony')} flush />

      {loading ? (
        <div className={styles.loading} role="status" aria-label={t('common.loading')}>
          <Spinner size={24} />
        </div>
      ) : (
        <>
          <ConnectorsGenomeMobile
            controller={apps}
            onBrowseTools={() => selectTab('tools')}
            onBrowseChannels={() => selectTab('channels')}
          />

          {/* The DS Tabs Group at Size=M — what the design's own mobile frame (Figma 13619-120713,
              225x36) specifies, so this strip and the desktop one are one component at two sizes. */}
          <Tabs
            containerRef={tabsRef}
            className={styles.tabs}
            items={CONNECTORS_TABS.map((v) => ({ value: v, label: t(`connectors.tab.${v}`) }))}
            value={tab}
            onChange={selectTab}
            size="medium"
            label={t('connectors.title')}
          />

          {/* Both tabs search now. The placeholder differs because the haystack does: the Tools tab
              searches connector names, the Channels tab its sections (and the AI-coder card titles). */}
          <Input
            className={styles.search}
            kind="search"
            type="search"
            value={tab === 'tools' ? query : channelQuery}
            onChange={(e) => (tab === 'tools' ? setQuery(e.target.value) : setChannelQuery(e.target.value))}
            onClear={() => (tab === 'tools' ? setQuery('') : setChannelQuery(''))}
            clearAriaLabel={t('search.dialog.clear')}
            placeholder={tab === 'tools' ? t('connectors.searchPlaceholder') : t('common.search')}
            aria-label={tab === 'tools' ? t('connectors.searchPlaceholder') : t('common.search')}
          />

          <div className={styles.content}>
            {tab === 'tools' ? (
              <ToolsMobile controller={apps} mcp={mcp} query={query} target={route.target} />
            ) : tab === 'channels-out' ? (
              <ChannelsOutBody target={route.target} query={channelQuery} />
            ) : (
              <ChannelsMobile target={route.target} query={channelQuery} />
            )}
          </div>
        </>
      )}
    </>
  );
}
